import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { createResourceLocatorString } from '../../util/routes';

import {
  Heading,
  H2,
  H3,
  Page,
  LayoutSingleColumn,
  NamedLink,
  PrimaryButton,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';
import openingWeekPlanImage from '../../assets/2154184023.avif';

import css from './HowItWorksPage.module.css';

/* ===========================================================
   Shared expert data — keeps mock previews in sync with the
   experts shown on the home page.
   =========================================================== */
const EXPERTS = [
  {
    name: 'Anita K.',
    experience: '8 years of experience',
    niche: 'Nail Salon',
    rating: '5.0',
    reviews: 92,
    nextSlot: 'Tomorrow, 10:00 AM',
    price: 75,
    image:
      'https://images.unsplash.com/photo-1669269628135-21636aefa17d?q=80&w=928&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
  {
    name: 'Jordan L.',
    experience: '5 years · 4 properties',
    niche: 'Airbnb',
    rating: '4.9',
    reviews: 47,
    nextSlot: 'Today, 4:30 PM',
    price: 80,
    image:
      'https://images.unsplash.com/photo-1569779213435-ba3167dde7cc?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
  {
    name: 'Sam P.',
    experience: '7 years · 3 cafés',
    niche: 'Coffee Shop',
    rating: '4.8',
    reviews: 64,
    nextSlot: 'Today, 7:15 PM',
    price: 90,
    image:
      'https://images.unsplash.com/photo-1739413323915-2a289cc024f0?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
];

const TESTIMONIALS = [
  {
    quote:
      'One 30-minute call helped me rethink a lease I was about to sign.',
    name: 'Maya R.',
    role: 'Aspiring Salon Owner',
    location: 'Phoenix, AZ',
  },
  {
    quote: 'This call gave me clearer direction.',
    name: 'Daniel K.',
    role: 'First-Time Entrepreneur',
    location: 'Austin, TX',
  },
  {
    quote:
      'Talking to someone who actually runs multiple Airbnbs answered questions I couldn\'t find anywhere online.',
    name: 'Lauren S.',
    role: 'Aspiring Airbnb Host',
    location: 'San Diego, CA',
  },
  {
    quote:
      'I left with practical ideas, vendor recommendations, and an opening-week checklist.',
    name: 'Priya M.',
    role: 'New Business Owner',
    location: 'Jersey City, NJ',
  },
  {
    quote:
      "I wish I'd booked this call before spending money on things I didn't need.",
    name: 'Alex T.',
    role: 'Aspiring Founder',
    location: 'Chicago, IL',
  },
  {
    quote:
      "Honest advice from someone who's already made the mistakes — and told me what not to do.",
    name: 'James W.',
    role: 'Coffee Shop Owner',
    location: 'Portland, OR',
  },
];

/* ===========================================================
   Mock product panels (CSS-only previews) — one per step.
   They make the page read like a real product, not a deck.
   =========================================================== */
const MockBrowse = () => {
  const [anita, jordan, sam] = EXPERTS;
  return (
    <div className={css.mockPanel}>
      <div className={css.mockHeader}>
        <span className={css.mockDot} />
        <span className={css.mockDot} />
        <span className={css.mockDot} />
        <span className={css.mockHeaderLabel}>Browse · All niches</span>
      </div>
      <div className={css.mockBody}>
        <div className={css.mockSearch}>
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none" aria-hidden>
            <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="m12.5 12.5 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span>Verified business owners</span>
        </div>
        <div className={css.mockChips}>
          <span className={`${css.mockChip} ${css.mockChipActive}`}>All</span>
          <span className={css.mockChip}>{anita.niche}</span>
          <span className={css.mockChip}>{jordan.niche}</span>
          <span className={css.mockChip}>{sam.niche}</span>
        </div>
        <div className={css.mockResults}>
          <div className={css.mockResultRow}>
            <span
              className={css.mockResultAvatar}
              style={{ backgroundImage: `url(${anita.image})` }}
            />
            <div className={css.mockResultBody}>
              <strong>{anita.name}</strong>
              <span>{anita.niche} · {anita.experience}</span>
            </div>
            <div className={css.mockResultMeta}>
              <span className={css.mockStars}>★ {anita.rating}</span>
              <strong>${anita.price}</strong>
            </div>
          </div>
          <div className={css.mockResultRow}>
            <span
              className={css.mockResultAvatar}
              style={{ backgroundImage: `url(${sam.image})` }}
            />
            <div className={css.mockResultBody}>
              <strong>{sam.name}</strong>
              <span>{sam.niche} · {sam.experience}</span>
            </div>
            <div className={css.mockResultMeta}>
              <span className={css.mockStars}>★ {sam.rating}</span>
              <strong>${sam.price}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MockBook = () => {
  const sam = EXPERTS[2];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const dates = [4, 5, 6, 7, 8, 9, 10];
  return (
    <div className={css.mockPanel}>
      <div className={css.mockHeader}>
        <span className={css.mockDot} />
        <span className={css.mockDot} />
        <span className={css.mockDot} />
        <span className={css.mockHeaderLabel}>Book · {sam.name}</span>
      </div>
      <div className={css.mockBody}>
        <div className={css.mockMonth}>
          <span>March 2026</span>
          <span className={css.mockMonthArrows}>
            <span>‹</span>
            <span>›</span>
          </span>
        </div>
        <div className={css.mockCalGrid}>
          {days.map((d, i) => (
            <span key={`d-${i}`} className={css.mockCalLabel}>
              {d}
            </span>
          ))}
          {dates.map(n => (
            <span
              key={`n-${n}`}
              className={`${css.mockCalDay} ${n === 7 ? css.mockCalDayActive : ''} ${
                n < 6 ? css.mockCalDayPast : ''
              }`}
            >
              {n}
            </span>
          ))}
        </div>
        <div className={css.mockSlots}>
          <span className={css.mockSlot}>10:00</span>
          <span className={`${css.mockSlot} ${css.mockSlotActive}`}>11:30</span>
          <span className={css.mockSlot}>14:00</span>
          <span className={css.mockSlot}>16:30</span>
        </div>
        <div className={css.mockConfirm}>
          <span>30 min · Today, 11:30 AM</span>
          <span className={css.mockConfirmBtn}>Confirm · ${sam.price}</span>
        </div>
      </div>
    </div>
  );
};

const MockPlan = () => {
  return (
    <img
      className={css.mockPlanImage}
      src={openingWeekPlanImage}
      alt="Your opening-week plan notes"
    />
  );
};

const STEPS = [
  {
    number: '01',
    eyebrow: 'Step one',
    title: 'Pick a niche, pick a person',
    description:
      "Browse business owners by industry, city, or experience. See what they've built and connect directly for advice.",
    details: [
      'Filter by industry, city, and experience',
      'Read full profiles with verified credentials',
      'Check ratings from previous calls',
    ],
    visual: <MockBrowse />,
  },
  {
    number: '02',
    eyebrow: 'Step two',
    title: 'Book a 30 or 60-minute call',
    description:
      'Choose a slot that fits your schedule. Pay securely through Stripe. You get a calendar invite and a Google Meet link in seconds.',
    details: [
      'Pick a time that fits — same-day slots common',
      'Stripe-secured payment, held until call completes',
      'Instant confirmation and calendar invite',
    ],
    visual: <MockBook />,
  },
  {
    number: '03',
    eyebrow: 'Step three',
    title: 'Walk away with clear next steps',
    description:
      'Talk through costs, leases, hiring, pricing, and other real-world business questions.',
    details: [
      'Personalized advice tailored to your situation',
      'Suggestions, resources, and vendor recommendations based on experience',
      'Get practical insights you can apply',
    ],
    visual: <MockPlan />,
  },
];

const DELIVERABLES = [
  {
    label: 'Concrete next steps',
    description: 'Practical next steps based on real experience.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M9 11l2 2 4-4M5 7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: 'Real numbers',
    description: 'Real-world examples of costs, margins, and benchmarks.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M4 19V5m4 14v-9m4 9V8m4 11v-6m4 6V4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    label: 'Vendor recommendations',
    description: "Recommendations on suppliers, software, and contractors they've personally used.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M3 7h18l-2 12H5L3 7Zm5 0V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: 'Pitfalls to avoid',
    description: 'Lessons learned from real business experience.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 3 2 21h20L12 3Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path d="M12 10v5M12 18.5v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

const PERSONAS = [
  {
    label: 'Just getting started',
    description:
      "You're thinking about starting a business and want real-world advice before taking the leap.",
    metric: 'Most-booked',
  },
  {
    label: 'Getting ready to open',
    description:
      'You need practical guidance on hiring, vendors, pricing, and operations.',
    metric: '~60% of bookings',
  },
  {
    label: 'Already operating',
    description:
      "You're already running a business and want perspective from someone who's been through similar challenges.",
    metric: 'Highest-rated',
  },
];

export const HowItWorksPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const isHoveringRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isHoveringRef.current) {
        setActiveTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const title = intl.formatMessage({
    id: 'HowItWorksPage.title',
    defaultMessage: 'How it Works — DoneThat',
  });

  const handleFindOwner = () => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <section className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroOrb} aria-hidden />
            <div className={css.heroInner}>
              <span className={css.eyebrow}>How it works</span>
              <Heading as="h1" rootClassName={css.heroHeading}>
                Three steps from <br/><em>information overload</em><br/>to real business advice.
              </Heading>
              <p className={css.heroDescription}>
                Find a verified business owner, book a video call, and get practical insights from
                someone who&apos;s been there. Book a 1:1 call within 24 hours.
              </p>
              <div className={css.heroActions}>
                <PrimaryButton className={css.heroCta} onClick={handleFindOwner}>
                  Find an owner
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </PrimaryButton>
                <NamedLink name="FAQPage" className={css.heroSecondary}>
                  Read the FAQ
                </NamedLink>
              </div>
            </div>
          </section>

          {/* ===== Steps (zigzag) ===== */}
          <section className={css.steps}>
            {STEPS.map((step, i) => (
              <article key={i} className={css.stepBlock}>
                <div className={css.stepCopy}>
                  <span className={css.stepEyebrow}>{step.eyebrow}</span>
                  <span className={css.stepNumeral} aria-hidden>
                    {step.number}
                  </span>
                  <H2 rootClassName={css.stepTitle}>{step.title}</H2>
                  <p className={css.stepDescription}>{step.description}</p>
                  <ul className={css.stepDetails}>
                    {step.details.map((d, j) => (
                      <li key={j} className={css.stepDetail}>
                        <span className={css.stepCheck} aria-hidden>
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path
                              d="m2.5 6 2.5 2.5L9.5 3.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={css.stepVisual}>{step.visual}</div>
              </article>
            ))}
          </section>

          {/* ===== Deliverables ===== */}
          <section className={css.deliverables}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>What you actually get</span>
              <H2 rootClassName={css.sectionHeading}>
                Practical insights <em>you can use</em>.
              </H2>
            </div>
            <div className={css.deliverablesGrid}>
              {DELIVERABLES.map((d, i) => (
                <div key={i} className={css.deliverableCard}>
                  <div className={css.deliverableIcon}>{d.icon}</div>
                  <H3 rootClassName={css.deliverableLabel}>{d.label}</H3>
                  <p className={css.deliverableDesc}>{d.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ===== Testimonials ===== */}
          <section className={css.testimonials}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Real wins</span>
              <H2 rootClassName={css.sectionHeading}>
                Real conversations. <em>Real outcomes.</em>
              </H2>
            </div>
            <div
              className={css.testimonialsCarousel}
              onMouseEnter={() => { isHoveringRef.current = true; }}
              onMouseLeave={() => { isHoveringRef.current = false; }}
            >
              <div
                className={css.testimonialsTrack}
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                {TESTIMONIALS.map((t, i) => (
                  <div key={i} className={css.testimonialCard}>
                    <span className={css.quoteMark} aria-hidden>
                      &ldquo;
                    </span>
                    <p className={css.testimonialQuote}>{t.quote}</p>
                    <div className={css.testimonialAuthor}>
                      <strong>{t.name}</strong>
                      <span>
                        {t.role} · {t.location}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={css.testimonialDots}>
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  className={`${css.dot} ${i === activeTestimonial ? css.dotActive : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </section>

          {/* ===== Personas ===== */}
          <section className={css.personas}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Who books these calls</span>
              <H2 rootClassName={css.sectionHeading}>
                Built for people starting and growing <em>real businesses</em>.
              </H2>
            </div>
            <div className={css.personasGrid}>
              {PERSONAS.map((p, i) => (
                <div key={i} className={css.personaCard}>
                  <span className={css.personaMetric}>{p.metric}</span>
                  <H3 rootClassName={css.personaLabel}>{p.label}</H3>
                  <p className={css.personaDesc}>{p.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ===== CTA ===== */}
          <section className={css.cta}>
            <div className={css.ctaPattern} aria-hidden />
            <div className={css.ctaInner}>
              <span className={css.ctaEyebrow}>Ready when you are</span>
              <H2 rootClassName={css.ctaHeading}>
                Skip the guesswork. Talk to someone <em>who&apos;s already built</em> the kind of business you want.
              </H2>
              <p className={css.ctaSubtitle}>
                Browse verified business owners, book a video call, and get practical insights from
                someone who&apos;s been there.
              </p>
              <div className={css.ctaActions}>
                <PrimaryButton className={css.ctaButton} onClick={handleFindOwner}>
                  Find a business owner
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </PrimaryButton>
                <NamedLink name="FAQPage" className={css.ctaSecondary}>
                  See FAQ
                </NamedLink>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedHowItWorksPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <HowItWorksPageComponent
      routeConfiguration={routeConfiguration}
      config={config}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HowItWorksPage = compose(connect(mapStateToProps))(EnhancedHowItWorksPage);

export default HowItWorksPage;
